import React from "react";

import { EntityType } from "constants/index";
import { usePrompt } from "hooks";
import { useDealActions, useViewActions } from "store";
import { IEntityTagData, IItemToShare, IShareDialogType } from "store/types";
import toIsoMidday from "utils/to-iso-midday";

import { IEntityTag } from "../../../store/actions/deal-actions/tag-entity-actions";
import { ClassificationPrompt } from "../../Deal-workspace/Workspace-metadata-components/Cells/Prompts/ClassificationPrompt";
import { EffectiveDatePrompt } from "../../Deal-workspace/Workspace-metadata-components/Cells/Prompts/EffectiveDatePrompt";
import { InvestorPrompt } from "../../Deal-workspace/Workspace-metadata-components/Cells/Prompts/InvestorPrompt";
import { OperationsStatusPrompt } from "../../Deal-workspace/Workspace-metadata-components/Cells/Prompts/OperationslStatusPrompt";
import { useWorkspace } from "../WorkspaceContext";

export const useWorkspaceCellHook = (refetchFolderMetadata: () => Promise<void>) => {
  const openPrompt = usePrompt();
  const { updateFileAction } = useDealActions();
  const { setShareDialog } = useViewActions();
  const { dispatchDetail } = useWorkspace();

  const handleClassificationUpdate = async (
    e: React.MouseEvent<HTMLDivElement>,
    fileId: number,
    currentClassification: string | null
  ) => {
    e.stopPropagation();
    const result = await openPrompt({
      componentType: "popover",
      anchorEl: e.currentTarget as HTMLElement,
      Component: ClassificationPrompt,
      props: { currentClassification }
    });
    if (!result) return;
    await updateFileAction({ fileId, document_type: result.classification });
    dispatchDetail({
      key: "patch",
      id: fileId,
      entityType: EntityType.FILE,
      patch: { document_type: result.classification }
    });
  };

  const handleInvestorUpdate = async ({
    e,
    fileId,
    currentInvestorId,
    dealId,
    allInvestors,
    splitByCommitment,
    investorIds,
    entireCommitment
  }: {
    e: React.MouseEvent<HTMLDivElement>;
    fileId: number;
    currentInvestorId: number | null;
    dealId?: number;
    allInvestors?: boolean;
    splitByCommitment?: boolean;
    investorIds?: number[];
    entireCommitment?: boolean;
  }) => {
    e.stopPropagation();
    const result = await openPrompt({
      componentType: "popover",
      anchorEl: e.currentTarget as HTMLElement,
      Component: InvestorPrompt,
      props: {
        currentInvestorId,
        dealId: dealId ?? null,
        currentAllInvestors: allInvestors ?? false,
        currentSplitByCommitment: splitByCommitment ?? false,
        currentInvestorIds: investorIds ?? [],
        currentEntireCommitment: entireCommitment ?? false,
        showGrouped: Boolean(dealId)
      }
    });
    if (!result) return;
    await updateFileAction({
      fileId,
      investor_id: result.investor?.id ?? null,
      all_investors: result.allInvestors,
      split_by_commitment: result.allInvestors ? true : result.splitByCommitment,
      investor_ids: result.investorIds,
      group_commitment: result.groupCommitment,
      entire_commitment: result.entireCommitment
    });
    dispatchDetail({
      key: "patch",
      id: fileId,
      entityType: EntityType.FILE,
      patch: result.allInvestors
        ? { investor: null, investor_ids: [], all_investors: true, group_commitment: false, entire_commitment: false }
        : {
            investor_ids: result.investorIds ?? [],
            investor: result.investor,
            all_investors: false,
            group_commitment: result.groupCommitment,
            entire_commitment: result.entireCommitment
          }
    });
  };

  const handleEffectiveDateUpdate = async (
    e: React.MouseEvent<HTMLDivElement>,
    fileId: number,
    currentEffectiveDate: string | null
  ) => {
    e.stopPropagation();
    const result = await openPrompt({
      componentType: "popover",
      anchorEl: e.currentTarget as HTMLElement,
      Component: EffectiveDatePrompt,
      props: { currentDate: currentEffectiveDate }
    });
    if (!result) return;
    const effective_date = result.effectiveDate ? toIsoMidday(result.effectiveDate) : null;
    await updateFileAction({ fileId, effective_date });
    dispatchDetail({ key: "patch", id: fileId, entityType: EntityType.FILE, patch: { effective_date } });
  };

  const handleOperationsStatusUpdate = async (
    e: React.MouseEvent<HTMLDivElement>,
    fileId: number,
    currentOperationsStatus: string | null
  ) => {
    e.stopPropagation();
    const result = await openPrompt({
      componentType: "popover",
      anchorEl: e.currentTarget as HTMLElement,
      Component: OperationsStatusPrompt,
      props: { currentStatus: currentOperationsStatus }
    });
    if (!result) return;
    await updateFileAction({ fileId, file_status: result.status });
    dispatchDetail({ key: "patch", id: fileId, entityType: EntityType.FILE, patch: { file_status: result.status } });
  };

  const handleShareUpdate = (
    e: React.MouseEvent<HTMLDivElement>,
    entityId: number,
    entityName: string,
    dealId: number | null,
    type: IShareDialogType,
    itemToShare?: IItemToShare
  ) => {
    e.stopPropagation();
    setShareDialog({
      elements: [entityId],
      type,
      shareName: entityName,
      dealId,
      itemToShare: itemToShare ?? null,
      cb: async () => {
        await refetchFolderMetadata();
      }
    });
  };

  const handleTagsSync = (entityId: number, entityType: string, tags: IEntityTag[]) => {
    const entity_tag_data: IEntityTagData[] = tags.map((tag) => ({ investor_id: tag.id, name: tag.name }));
    dispatchDetail({ key: "patch", id: entityId, entityType, patch: { entity_tag_data } });
  };

  return {
    handleClassificationUpdate,
    handleInvestorUpdate,
    handleEffectiveDateUpdate,
    handleOperationsStatusUpdate,
    handleShareUpdate,
    handleTagsSync
  };
};
